import type { ApiResponse } from '@/types/api';
import { apiClient } from './api';
import { apiEndpoints } from '@/config/app';

// Analytics types
export interface AgentPerformanceMetrics {
  agentId: string;
  name: string;
  email: string;
  role: string;
  department?: string;
  avatarUrl?: string;
  status: 'online' | 'offline' | 'busy' | 'away';
  metrics: {
    ticketsResolved: number;
    ticketsInProgress: number;
    averageResponseTime: number; // in minutes
    averageResolutionTime: number; // in hours
    firstContactResolution: number; // percentage
    customerSatisfaction: number; // 1-5 scale
    slaCompliance: number; // percentage
    escalationRate: number; // percentage
    totalInteractions: number;
    productivityScore: number; // 0-100
  };
  trends: {
    daily: Array<{ date: string; resolved: number; created: number; responseTime: number }>;
    weekly: Array<{ week: string; resolved: number; satisfaction: number }>;
  };
  comparison: {
    vsTeamAverage: number; // percentage difference
    vsLastPeriod: number; // percentage change
    ranking: number; // position in team
    totalAgents: number;
  };
}

export interface TeamMetrics {
  totalAgents: number;
  activeAgents: number;
  averageMetrics: {
    ticketsPerAgent: number;
    responseTime: number;
    resolutionTime: number;
    satisfaction: number;
    slaCompliance: number;
  };
  topPerformers: AgentPerformanceMetrics[];
  needsImprovement: AgentPerformanceMetrics[];
}

export interface ExecutiveDashboardMetrics {
  totalTickets: number;
  openTickets: number;
  closedTickets: number;
  avgResponseTime: number; // hours
  avgResolutionTime: number; // hours
  customerSatisfactionAvg: number; // 1-5
  slaComplianceRate: number; // percentage
  agentUtilization: number; // percentage
  ticketTrends: Array<{
    date: string;
    created: number;
    resolved: number;
    backlog: number;
  }>;
  categoryDistribution: Array<{
    category: string;
    count: number;
    percentage: number;
  }>;
  priorityDistribution: Array<{
    priority: string;
    count: number;
    percentage: number;
  }>;
}

export interface OperationalMetrics {
  currentLoad: {
    totalOpenTickets: number;
    averageAgeHours: number;
    overdueTickets: number;
    escalatedTickets: number;
  };
  agentStatus: Array<{
    agentId: string;
    name: string;
    status: 'online' | 'offline' | 'busy' | 'away';
    activeTickets: number;
    lastActivity: string;
  }>;
  realtimeStats: {
    ticketsCreatedToday: number;
    ticketsResolvedToday: number;
    avgResponseTimeToday: number;
    activeChats: number;
  };
}

export interface DateRangeParams {
  from: Date;
  to: Date;
}

export interface AgentPerformanceQueryParams {
  dateRange: DateRangeParams;
  agentIds?: string[];
  includeInactive?: boolean;
}

/**
 * The row shape actually returned by GET /analytics/agents/performance.
 */
interface BackendAgentPerformanceRow {
  agentId?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
  status?: string;
  assignedTickets?: number;
  resolvedToday?: number;
  resolvedThisWeek?: number;
  resolvedThisMonth?: number;
  averageResponseTimeHours?: number;
  averageResolutionTimeHours?: number;
  customerSatisfactionScore?: number;
  satisfactionSurveyCount?: number;
  firstContactResolutionRate?: number;
  slaComplianceRate?: number;
  productivityScore?: number;
  utilizationRate?: number;
}

type AgentStatus = AgentPerformanceMetrics['status'];

/** The backend sends a free-form status string ("Online"); the UI switches on a union. */
const normaliseStatus = (status?: string): AgentStatus => {
  const value = (status || '').trim().toLowerCase();
  return value === 'online' || value === 'busy' || value === 'away' || value === 'offline'
    ? value
    : 'offline';
};

const mapAgentRow = (row: BackendAgentPerformanceRow): AgentPerformanceMetrics => ({
  agentId: row.agentId || '',
  name: row.name || 'Unknown agent',
  email: row.email || '',
  role: 'Agent',
  avatarUrl: row.avatarUrl || undefined,
  status: normaliseStatus(row.status),
  metrics: {
    ticketsResolved: row.resolvedThisMonth ?? 0,
    ticketsInProgress: row.assignedTickets ?? 0,
    // The frontend type is minutes; the backend reports hours.
    averageResponseTime: Math.round((row.averageResponseTimeHours ?? 0) * 60),
    averageResolutionTime: row.averageResolutionTimeHours ?? 0,
    firstContactResolution: row.firstContactResolutionRate ?? 0,
    customerSatisfaction: row.customerSatisfactionScore ?? 0,
    slaCompliance: row.slaComplianceRate ?? 0,
    // No backend source for these two. Left at zero rather than invented.
    escalationRate: 0,
    totalInteractions: 0,
    productivityScore: row.productivityScore ?? 0,
  },
  // The backend exposes no historical series for an agent, so the page must render an
  // empty trend state rather than a fabricated one.
  trends: { daily: [], weekly: [] },
  comparison: { vsTeamAverage: 0, vsLastPeriod: 0, ranking: 0, totalAgents: 0 },
});

const average = (values: number[]): number =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

/** Ranking and team-relative comparison are computed from the returned rows, in place. */
const rankAgents = (agents: AgentPerformanceMetrics[]): void => {
  const teamAverage = average(agents.map(a => a.metrics.ticketsResolved));
  const ordered = [...agents].sort((a, b) => b.metrics.ticketsResolved - a.metrics.ticketsResolved);

  agents.forEach(agent => {
    agent.comparison = {
      vsTeamAverage: teamAverage > 0
        ? Math.round(((agent.metrics.ticketsResolved - teamAverage) / teamAverage) * 100)
        : 0,
      // No prior-period data is fetched, so this stays neutral rather than guessing.
      vsLastPeriod: 0,
      ranking: ordered.findIndex(a => a.agentId === agent.agentId) + 1,
      totalAgents: agents.length,
    };
  });
};

const buildTeamMetrics = (agents: AgentPerformanceMetrics[]): TeamMetrics => ({
  totalAgents: agents.length,
  activeAgents: agents.filter(a => a.status !== 'offline').length,
  averageMetrics: {
    ticketsPerAgent: average(agents.map(a => a.metrics.ticketsResolved)),
    responseTime: average(agents.map(a => a.metrics.averageResponseTime)),
    resolutionTime: average(agents.map(a => a.metrics.averageResolutionTime)),
    satisfaction: average(agents.map(a => a.metrics.customerSatisfaction)),
    slaCompliance: average(agents.map(a => a.metrics.slaCompliance)),
  },
  topPerformers: [...agents]
    .sort((a, b) => b.metrics.ticketsResolved - a.metrics.ticketsResolved)
    .slice(0, 3),
  needsImprovement: [...agents]
    .sort((a, b) => a.metrics.ticketsResolved - b.metrics.ticketsResolved)
    .slice(0, 2),
});

export class AnalyticsService {
  /**
   * Get executive dashboard metrics
   */
  async getExecutiveDashboard(dateRange?: DateRangeParams): Promise<ApiResponse<ExecutiveDashboardMetrics>> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('from', dateRange.from.toISOString());
      params.append('to', dateRange.to.toISOString());
    }
    
    const url = `${apiEndpoints.analytics.executive}${params.toString() ? '?' + params.toString() : ''}`;
    return await apiClient.get<ExecutiveDashboardMetrics>(url);
  }

  /**
   * Get operational metrics for real-time dashboard
   */
  async getOperationalMetrics(): Promise<ApiResponse<OperationalMetrics>> {
    return await apiClient.get<OperationalMetrics>(apiEndpoints.analytics.operational);
  }

  /**
   * Get agent performance.
   *
   * The deployed backend exposes GET /analytics/agents/performance and returns a FLAT
   * array of per-agent rows keyed by fromDate/toDate. The page consumes
   * {agents, teamMetrics}, so the team roll-up and each agent's ranking are derived here
   * from the returned rows. Nothing is invented: fields the backend does not supply
   * (trends, escalation rate, interaction counts) stay empty or zero so the page can
   * show an honest empty state rather than fabricated analytics.
   */
  async getAgentPerformance(params: AgentPerformanceQueryParams): Promise<ApiResponse<{
    agents: AgentPerformanceMetrics[];
    teamMetrics: TeamMetrics;
  }>> {
    const queryParams = new URLSearchParams();
    queryParams.append('fromDate', params.dateRange.from.toISOString());
    queryParams.append('toDate', params.dateRange.to.toISOString());

    // The backend filters on a single optional agentId; it has no includeInactive filter.
    if (params.agentIds && params.agentIds.length === 1) {
      queryParams.append('agentId', params.agentIds[0]);
    }

    const url = `${apiEndpoints.analytics.agentPerformance}?${queryParams.toString()}`;
    const response = await apiClient.get<BackendAgentPerformanceRow[]>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to load agent performance');
    }

    // A 200 carrying the SPA shell used to reach here as a 'successful' string.
    if (!Array.isArray(response.data)) {
      throw new Error('Agent performance data was not returned in the expected format');
    }

    const agents = response.data.map(mapAgentRow);
    rankAgents(agents);

    return {
      success: true,
      data: { agents, teamMetrics: buildTeamMetrics(agents) },
      message: response.message,
    };
  }

  /**
   * Get ticket trends over time
   */
  async getTicketTrends(dateRange: DateRangeParams, groupBy: 'day' | 'week' | 'month' = 'day'): Promise<ApiResponse<Array<{
    date: string;
    created: number;
    resolved: number;
    backlog: number;
  }>>> {
    const params = new URLSearchParams();
    params.append('from', dateRange.from.toISOString());
    params.append('to', dateRange.to.toISOString());
    params.append('groupBy', groupBy);

    const url = `${apiEndpoints.analytics.ticketTrends}?${params.toString()}`;
    return await apiClient.get<Array<{
      date: string;
      created: number;
      resolved: number;
      backlog: number;
    }>>(url);
  }

  /**
   * Get category distribution
   */
  async getCategoryDistribution(dateRange?: DateRangeParams): Promise<ApiResponse<Array<{
    category: string;
    count: number;
    percentage: number;
  }>>> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('from', dateRange.from.toISOString());
      params.append('to', dateRange.to.toISOString());
    }

    const url = `${apiEndpoints.analytics.categoryDistribution}${params.toString() ? '?' + params.toString() : ''}`;
    return await apiClient.get<Array<{
      category: string;
      count: number;
      percentage: number;
    }>>(url);
  }

  /**
   * Get customer satisfaction trends
   */
  async getCustomerSatisfactionTrends(dateRange: DateRangeParams): Promise<ApiResponse<Array<{
    date: string;
    averageRating: number;
    responseCount: number;
  }>>> {
    const params = new URLSearchParams();
    params.append('from', dateRange.from.toISOString());
    params.append('to', dateRange.to.toISOString());

    const url = `/analytics/csat-trends?${params.toString()}`;
    return await apiClient.get<Array<{
      date: string;
      averageRating: number;
      responseCount: number;
    }>>(url);
  }

  /**
   * Get SLA compliance report
   */
  async getSLAComplianceReport(dateRange?: DateRangeParams): Promise<ApiResponse<{
    overallCompliance: number;
    byPriority: Array<{
      priority: string;
      compliance: number;
      totalTickets: number;
      breachedTickets: number;
    }>;
    byAgent: Array<{
      agentId: string;
      agentName: string;
      compliance: number;
      totalTickets: number;
    }>;
  }>> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('from', dateRange.from.toISOString());
      params.append('to', dateRange.to.toISOString());
    }

    const url = `/analytics/sla-compliance${params.toString() ? '?' + params.toString() : ''}`;
    return await apiClient.get<{
      overallCompliance: number;
      byPriority: Array<{
        priority: string;
        compliance: number;
        totalTickets: number;
        breachedTickets: number;
      }>;
      byAgent: Array<{
        agentId: string;
        agentName: string;
        compliance: number;
        totalTickets: number;
      }>;
    }>(url);
  }

  /**
   * Get team workload distribution
   */
  async getTeamWorkloadDistribution(): Promise<ApiResponse<Array<{
    agentId: string;
    agentName: string;
    activeTickets: number;
    capacity: number;
    utilization: number; // percentage
    status: 'online' | 'offline' | 'busy' | 'away';
  }>>> {
    return await apiClient.get<Array<{
      agentId: string;
      agentName: string;
      activeTickets: number;
      capacity: number;
      utilization: number;
      status: 'online' | 'offline' | 'busy' | 'away';
    }>>('/analytics/team-workload');
  }

  /**
   * Export analytics report
   */
  async exportReport(
    reportType: 'agent-performance' | 'executive-summary' | 'sla-compliance',
    dateRange: DateRangeParams,
    format: 'pdf' | 'excel' | 'csv' = 'pdf'
  ): Promise<ApiResponse<{ downloadUrl: string }>> {
    const params = new URLSearchParams();
    params.append('type', reportType);
    params.append('format', format);
    params.append('from', dateRange.from.toISOString());
    params.append('to', dateRange.to.toISOString());

    const url = `/analytics/export?${params.toString()}`;
    return await apiClient.get<{ downloadUrl: string }>(url);
  }

  /**
   * Get real-time dashboard data
   */
  async getRealtimeDashboardData(): Promise<ApiResponse<{
    onlineAgents: number;
    activeTickets: number;
    avgResponseTime: number;
    recentActivity: Array<{
      type: 'ticket_created' | 'ticket_resolved' | 'agent_login' | 'agent_logout';
      timestamp: string;
      agentName?: string;
      ticketId?: string;
      message: string;
    }>;
  }>> {
    return await apiClient.get<{
      onlineAgents: number;
      activeTickets: number;
      avgResponseTime: number;
      recentActivity: Array<{
        type: 'ticket_created' | 'ticket_resolved' | 'agent_login' | 'agent_logout';
        timestamp: string;
        agentName?: string;
        ticketId?: string;
        message: string;
      }>;
    }>('/analytics/realtime-dashboard');
  }
}

// Create and export singleton instance
export const analyticsService = new AnalyticsService();